import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Tactiq", url: "https://tactiq.io" };

const en: ComparePageCopy = {
  seo: {
    title: "Tactiq vs. Minuta — transcripts after vs. answers during",
    description:
      "Tactiq is built for Chrome transcript workflows. Minuta is built for live calls in English, Portuguese, and Spanish where you need answer drafts from your context and docs while the meeting is still happening.",
    keywords:
      "tactiq alternative, tactiq vs minuta, no bot meeting transcription, no bot meeting copilot, real-time meeting copilot mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Tactiq vs. Minuta: transcripts after vs. answers during",
  intro:
    "Tactiq is the better fit when your workflow starts with a browser transcript: capture the Google Meet tab, clean up notes, and move them into Google Workspace. Minuta is for a different moment: you are still in the call, someone asks a question in English, Portuguese, or Spanish, and you need an answer drafted from your context and docs before the conversation moves on.",
  honesty: {
    heading: "Pick the right one",
    pickThem: {
      name: "Tactiq",
      bullets: [
        "You live inside Google Meet on Chrome and want a transcript embedded in the browser tab, without installing a desktop app.",
        "You're a heavy Google Workspace user (Docs, Drive, Calendar) and want meeting artifacts piped straight there.",
        "Your priority is the after-call record: transcript, notes, and summary you can search or share later.",
        "You want Chromebook / Linux / multi-OS coverage that a Chrome extension naturally gives you.",
        "You do not need live answer drafts from your own context while the conversation is happening.",
      ],
    },
    pickMinuta: [
      "You need a real-time copilot drafting answers in English, Portuguese, or Spanish during the call.",
      "You need the draft grounded in your context and docs, not just a transcript after the meeting ends.",
      "You need EN ↔ PT/ES live translation while the call is happening — Tactiq does not do live translation.",
      "The call is on a desktop client (native Zoom app, native Teams app, FaceTime, anything not Chrome) — Minuta captures system audio, no extension dependency.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Tactiq", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot in the meeting",
        them: {
          value: "No — browser-tab transcript",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — system audio capture (both products win here)" },
      },
      {
        feature: "Where it runs",
        them: {
          value: "Chrome extension (any OS with Chrome)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Native macOS app (Windows soon)" },
      },
      {
        feature: "Real-time answer copilot",
        them: { value: "No — focused on transcript and post-call notes" },
        minuta: { value: "Yes (Pro) — drafts answers as questions land" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Yes — real-time" },
      },
      {
        feature: "Audio processing",
        them: {
          value: "Cloud",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Where transcripts and notes live",
        them: {
          value: "Tactiq cloud (with Google Drive/Docs export)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "On your computer (transient processing)" },
      },
      {
        feature: "Native Zoom app / Teams app / FaceTime",
        them: { value: "No — Chrome only" },
        minuta: { value: "Yes — anything that plays through system audio" },
      },
      {
        feature: "Google Workspace integration",
        them: {
          value: "Native (Docs / Drive / Calendar)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Manual export" },
      },
      {
        feature: "Native PT/ES website",
        them: { value: "No — English-only site" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Pricing entry",
        them: {
          value: "Free (10 meetings/mo) · Pro $12/mo · Team $20/u/mo",
          cite: { url: "https://tactiq.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-05-01; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "Answers during the call. Tactiq is stronger when you want a browser transcript and clean notes after. Minuta drafts what to say next while the question is still in the air.",
      "Context-aware drafting. Minuta can use your context and docs to shape the answer, instead of leaving you to interpret a transcript later.",
      "Live translation. Tactiq does not translate the meeting in real time. Minuta surfaces EN ↔ PT/ES translation while you're talking.",
      "No bot, any platform. Tactiq is Chrome-extension-bound to Google Meet. Minuta works in Zoom, Teams, Meet, and browser calls from your desktop without joining the call.",
      "Beyond the browser. Tactiq is Chrome-only. Minuta captures system audio — native Zoom, Teams, FaceTime, Discord, anything that plays through your speakers.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Keep the transcript. Add live answers.",
    body: "7 days with everything unlocked. Bring your context, get answer drafts during the call, and keep the bot out.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Tactiq vs. Minuta — transcrição depois vs. respostas durante",
    description:
      "Tactiq foi feito para fluxos de transcrição no Chrome. Minuta foi feito para calls em inglês, português e espanhol em que você precisa de respostas a partir do seu contexto e docs enquanto a reunião acontece.",
    keywords:
      "alternativa tactiq, tactiq vs minuta, transcrição de reunião sem bot, copiloto de reunião em tempo real, transcrição de reunião no mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Tactiq vs. Minuta: transcrição depois vs. respostas durante",
  intro:
    "Tactiq é a melhor escolha quando seu fluxo começa com transcrição no navegador: capturar a aba do Google Meet, organizar notas e levar tudo para o Google Workspace. Minuta serve outro momento: você ainda está na call, alguém faz uma pergunta em inglês, português ou espanhol, e você precisa de uma resposta sugerida a partir do seu contexto e docs antes da conversa andar.",
  honesty: {
    heading: "Escolha o certo",
    pickThem: {
      name: "Tactiq",
      bullets: [
        "Você vive no Google Meet via Chrome e quer transcrição na própria aba, sem instalar app desktop.",
        "Você é heavy user de Google Workspace (Docs, Drive, Calendar) e quer os artefatos da reunião indo direto pra lá.",
        "Sua prioridade é o registro depois da call: transcript, notas e resumo para buscar ou compartilhar mais tarde.",
        "Você quer rodar em Chromebook / Linux / multi-SO, coisa que extensão de Chrome resolve.",
        "Você não precisa de respostas ao vivo a partir do seu próprio contexto enquanto a conversa acontece.",
      ],
    },
    pickMinuta: [
      "Você precisa de um copiloto sugerindo respostas em inglês, português ou espanhol durante a call.",
      "Você precisa que a sugestão use seu contexto e docs, não apenas um transcript depois que a reunião acaba.",
      "Você precisa de tradução ao vivo EN ↔ PT/ES enquanto a reunião acontece — Tactiq não faz tradução ao vivo.",
      "Você quer privacidade — processamento com retenção zero e sem bots na call.",
      "A call é no app nativo (Zoom desktop, Teams desktop, FaceTime, qualquer coisa fora do Chrome) — Minuta captura o áudio do sistema, sem depender de extensão.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Recurso", them: "Tactiq", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot na reunião",
        them: {
          value: "Não — transcrição na aba do navegador",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Não — captura do áudio do sistema (os dois ganham aqui)" },
      },
      {
        feature: "Onde roda",
        them: {
          value: "Extensão Chrome (qualquer SO com Chrome)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "App nativo macOS (Windows em breve)" },
      },
      {
        feature: "Copiloto sugerindo resposta em tempo real",
        them: { value: "Não — foco em transcript e notas pós-call" },
        minuta: { value: "Sim (Pro) — sugere resposta no momento da pergunta" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: { value: "Não" },
        minuta: { value: "Sim — tempo real" },
      },
      {
        feature: "Processamento do áudio",
        them: {
          value: "Cloud",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Onde a transcrição e ata ficam",
        them: {
          value: "Cloud do Tactiq (com export pra Google Drive/Docs)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No seu computador (processamento temporário)" },
      },
      {
        feature: "Zoom desktop / Teams desktop / FaceTime",
        them: { value: "Não — só Chrome" },
        minuta: { value: "Sim — qualquer coisa que toque pelo áudio do sistema" },
      },
      {
        feature: "Integração Google Workspace",
        them: {
          value: "Nativa (Docs / Drive / Calendar)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Export manual" },
      },
      {
        feature: "Site nativo em PT/ES",
        them: { value: "Não — só em inglês" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Preço de entrada",
        them: {
          value: "Grátis (10 reuniões/mês) · Pro US$ 12/mês · Team US$ 20/u/mês",
          cite: { url: "https://tactiq.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mês" },
      },
    ],
    footnote: "Preços referência 2026-05-01; confirme com cada empresa pra valores atuais.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "Respostas durante a call. Tactiq é mais forte quando você quer transcrição no navegador e notas limpas depois. Minuta sugere o que dizer enquanto a pergunta ainda está no ar.",
      "Sugestão com contexto. Minuta pode usar seu contexto e docs para moldar a resposta, em vez de te deixar interpretar um transcript mais tarde.",
      "Tradução ao vivo. Tactiq não traduz a reunião em tempo real. Minuta mostra tradução EN ↔ PT/ES enquanto você fala.",
      "Sem bot, qualquer plataforma. O Tactiq é uma extensão do Chrome limitada ao Google Meet. O Minuta funciona no Zoom, Teams, Meet e qualquer call no navegador a partir do desktop, sem entrar na reunião.",
      "Além do navegador. Tactiq é só Chrome. Minuta captura o áudio do sistema — Zoom nativo, Teams nativo, FaceTime, Discord, qualquer coisa que toque no alto-falante.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Mantenha a transcrição. Some respostas ao vivo.",
    body: "7 dias com tudo desbloqueado. Traga seu contexto, receba sugestões durante a call e mantenha o bot fora.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Tactiq vs. Minuta — transcripción después vs. respuestas durante",
    description:
      "Tactiq está hecho para flujos de transcripción en Chrome. Minuta está hecho para calls en inglés, portugués y español donde necesitas respuestas desde tu contexto y docs mientras la reunión sigue.",
    keywords:
      "alternativa tactiq, tactiq vs minuta, transcripción de reunión sin bot, copiloto de reunión en tiempo real, transcripción de reunión mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Tactiq vs. Minuta: transcripción después vs. respuestas durante",
  intro:
    "Tactiq es la mejor opción cuando tu flujo empieza con una transcripción en el navegador: capturar la pestaña de Google Meet, ordenar notas y moverlas a Google Workspace. Minuta sirve otro momento: sigues en la call, alguien hace una pregunta en inglés, portugués o español, y necesitas una respuesta sugerida desde tu contexto y docs antes de que la conversación avance.",
  honesty: {
    heading: "Elige el correcto",
    pickThem: {
      name: "Tactiq",
      bullets: [
        "Vives dentro de Google Meet en Chrome y quieres la transcripción en la propia pestaña, sin instalar una app de escritorio.",
        "Eres heavy user de Google Workspace (Docs, Drive, Calendar) y quieres que los artefactos de la reunión vayan directo allí.",
        "Tu prioridad es el registro después de la call: transcript, notas y resumen para buscar o compartir más tarde.",
        "Quieres correr en Chromebook / Linux / multi-SO, algo que una extensión de Chrome resuelve fácil.",
        "No necesitas respuestas en vivo desde tu propio contexto mientras la conversación está pasando.",
      ],
    },
    pickMinuta: [
      "Necesitas un copiloto sugiriendo respuestas en inglés, portugués o español durante la call.",
      "Necesitas que la sugerencia use tu contexto y docs, no solo un transcript cuando la reunión termina.",
      "Necesitas traducción en vivo EN ↔ PT/ES mientras la reunión sucede — Tactiq no traduce en vivo.",
      "Quieres privacidad — procesamiento con retención cero y sin bots en la call.",
      "La call es en el cliente nativo (Zoom desktop, Teams desktop, FaceTime, cualquier cosa fuera de Chrome) — Minuta captura el audio del sistema, sin depender de extensión.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Tactiq", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot en la reunión",
        them: {
          value: "No — transcripción en la pestaña del navegador",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — captura del audio del sistema (ambos ganan aquí)" },
      },
      {
        feature: "Dónde corre",
        them: {
          value: "Extensión Chrome (cualquier SO con Chrome)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "App nativa macOS (Windows pronto)" },
      },
      {
        feature: "Copiloto sugiriendo respuesta en tiempo real",
        them: { value: "No — foco en transcript y notas post-call" },
        minuta: { value: "Sí (Pro) — sugiere respuesta en el momento" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Sí — tiempo real" },
      },
      {
        feature: "Procesamiento del audio",
        them: {
          value: "Cloud",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Dónde viven la transcripción y el acta",
        them: {
          value: "Cloud de Tactiq (con export a Google Drive/Docs)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "En tu computadora (procesamiento transitorio)" },
      },
      {
        feature: "Zoom desktop / Teams desktop / FaceTime",
        them: { value: "No — solo Chrome" },
        minuta: { value: "Sí — cualquier cosa que suene por el audio del sistema" },
      },
      {
        feature: "Integración Google Workspace",
        them: {
          value: "Nativa (Docs / Drive / Calendar)",
          cite: { url: "https://tactiq.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Export manual" },
      },
      {
        feature: "Web nativa en PT/ES",
        them: { value: "No — solo en inglés" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Precio de entrada",
        them: {
          value: "Gratis (10 reuniones/mes) · Pro US$ 12/mes · Team US$ 20/u/mes",
          cite: { url: "https://tactiq.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-05-01; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "Respuestas durante la call. Tactiq es más fuerte cuando quieres transcripción en el navegador y notas limpias después. Minuta sugiere qué decir mientras la pregunta sigue en el aire.",
      "Sugerencia con contexto. Minuta puede usar tu contexto y docs para dar forma a la respuesta, en vez de dejarte interpretar un transcript más tarde.",
      "Traducción en vivo. Tactiq no traduce la reunión en tiempo real. Minuta muestra traducción EN ↔ PT/ES mientras hablas.",
      "Sin bot, cualquier plataforma. Tactiq es una extensión de Chrome limitada a Google Meet. Minuta funciona en Zoom, Teams, Meet y cualquier call en el navegador desde tu escritorio, sin unirse a la reunión.",
      "Más allá del navegador. Tactiq es solo Chrome. Minuta captura el audio del sistema — Zoom nativo, Teams nativo, FaceTime, Discord, cualquier cosa que suene en el altavoz.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Mantén la transcripción. Suma respuestas en vivo.",
    body: "7 días con todo desbloqueado. Trae tu contexto, recibe sugerencias durante la call y mantén el bot fuera.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareTactiqCopy: ComparePageCopyByLang = { en, pt, es };
